import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
  useMemo,
} from "react";
import { fnGetDataSet, fnGetTableById, fnSave } from "@shared/api/restApi";
import {
  fnPrintByReport,
  getAutoSlip,
  getInitPopUpData,
} from "@shared/utils/CommonUtils";
import { showDialog } from "@widgets/CommonDialog";
import CommonSpinner from "@widgets/CommonSpinner";
import { global } from "@shared/libs/Global";
import SnGrid from "@shared/components/GridV2/SnGrid";
import SnTab from "@shared/components/SnTab";
import SnTabHeader from "@shared/components/SnTabHeader";
import SnForm from "@widgets/SnForm";
import SnButton from "@shared/components/SnButton";
import SnBarcodeBox from "@shared/components/SnBarcodeBox";
import SnSwitch from "@shared/components/SnSwitch";
import SnFileUploadBox from "@shared/components/SnFileUploadBox";
import { menuInfo } from "@shared/type/CommonType";
import { dateToString } from "@shared/utils/DateUtils";
import { showResizablePopupModal } from "@widgets/CommonModal";
import WP3002_POP_001 from "@menu/PRD/WRK_REG_POPUP/WP3002_POP_001";
import {
  collectRowsFromGrid,
  isYnY,
  resolveWrkRegPopupTables,
} from "./WP_POPUP_COMMON";
import { buildNoPltParam } from "../PRD_LABEL_PRINT";

interface popupProps {
  selectedData?: any;
  Menuprops?: menuInfo;
}

// 이 팝업의 탭 키(SnTab.selectedTab 문자열).
// WP_POPUP_COMMON의 TAB_REJECT/TAB_MATERIAL은 불량/투입자재 탭을 가진
// 다른 팝업 기준이라 이 화면에서는 쓰지 않는다.
const TAB_RUN = "1"; // 생산실적 (검사항목 탭은 "2")

const WP3002 = forwardRef(
  ({ selectedData, Menuprops }: popupProps, ref: React.Ref<any>) => {
    const { userInfo } = global();
    const formPanel1Ref = useRef<any>(null);
    const formPanel2Ref = useRef<any>(null);
    const popupRootRef = useRef<HTMLDivElement>(null);
    const panel2WrapperRef = useRef<HTMLDivElement | null>(null);
    const prodBarcodeRef = useRef<HTMLInputElement | null>(null);
    const gridRefs = useRef<{ [key: string]: React.RefObject<any> }>({
      grid1: React.createRef(),
      grid2: React.createRef(),
    });
    const getGridCurrent = (key: "grid1" | "grid2") => {
      return gridRefs.current[key].current;
    };
    const GRID_NO_EVENT = "noEvent";
    const isRunViewMode = !!(selectedData?.no_run || "").toString().trim();

    const [panel1Controls, setPanel1Controls] = useState<any[]>([]);
    const [panel2Controls, setPanel2Controls] = useState<any[]>([]);
    const [panel1Data, setPanel1Data] = useState<any>({});
    const [panel2Data, setPanel2Data] = useState<any>({});
    const [columns, setColums] = useState<any>({
      grid1: [],
      grid2: [],
    });
    const [dataSources, setDataSources] = useState<any>({
      grid1: [],
      grid2: [],
    });
    const [selectedDocu, setSelectedDocu] = useState<string>("");
    const [selectedLineDocu, setSelectedLineDocu] = useState<number>(0);
    const [tab, setTab] = useState<string>(TAB_RUN);
    const [popupParentEl, setPopupParentEl] = useState<HTMLElement | null>(
      null
    );
    const pendingRunlQueueRef = useRef<any[]>([]);
    const shouldRestoreProdFocusRef = useRef<boolean>(false);
    const isAutoSavingRef = useRef<boolean>(false);
    const hasRunlInspRowsRef = useRef<boolean>(false);
    const missingCartonReportNotifiedRef = useRef<boolean>(false);
    // onSearch가 grid1에 넣은 빈 배열이 AG Grid rowData에 실제 반영됐는지 추적한다.
    // true인 동안은 재바인딩이 아직 도착하지 않은 상태라, 이 시점에 addRow한 행은
    // 뒤늦게 도착한 재바인딩에 지워진다(태블릿처럼 렌더가 느린 기기에서 재현).
    const grid1RebindPendingRef = useRef<boolean>(false);
    const grid1RebindResolverRef = useRef<(() => void) | null>(null);
    const [prodBarcode, setProdBarcode] = useState<string>("");
    const [isCartonBarcodeAutoAvailable, setIsCartonBarcodeAutoAvailable] =
      useState<boolean>(false);
    const [isCartonBarcodeAutoMode, setIsCartonBarcodeAutoMode] =
      useState<boolean>(false);
    const [nmCartonReport, setNmCartonReport] = useState<string>("");
    const [manualCartonBarcode, setManualCartonBarcode] = useState<string>("");
    // 저장 동안 팝업을 덮는 스피너
    const [isSaving, setIsSaving] = useState<boolean>(false);

    const toNum = (v: any) => Number(v ?? 0) || 0;

    const toReadOnlyControls = (controls: any[]) => {
      if (!isRunViewMode) return controls;
      return (Array.isArray(controls) ? controls : []).map((control: any) => ({
        ...control,
        dc_editcon: "true",
      }));
    };

    // 제품실적 스캔 입력칸 포커스
    const focusProductBarcode = () => {
      setTimeout(() => {
        prodBarcodeRef.current?.focus();
        prodBarcodeRef.current?.select?.();
      }, 50);
    };

    // grid2(검사항목) 전체 row를 수집한다.
    // api가 일시적으로 비어있는 타이밍에는 getChange(insert/update)를 fallback으로 사용한다.
    const getAllGrid2Rows = () => {
      const grid2Ref = gridRefs.current["grid2"].current;
      return collectRowsFromGrid(grid2Ref, dataSources?.grid2, true);
    };

    // 실적수량(qt_w/qt_b/qt)을 grid1의 현재 행 기준으로 다시 계산해 실적정보 패널에 반영한다.
    // 스캔/행삭제가 같은 함수를 쓰므로 누적·차감 로직을 따로 두지 않는다.
    const recalcRunQtyToPanel = async () => {
      let qtW = 0;
      let qtB = 0;
      gridRefs.current["grid1"].current?.forEachNode?.((node: any) => {
        if (!node?.data) return;
        qtW += toNum(node.data.qt_w);
        qtB += toNum(node.data.qt_b);
      });
      const qt = qtW + qtB;

      await formPanel2Ref.current?.setDataValue?.("qt_w", qtW, GRID_NO_EVENT);
      await formPanel2Ref.current?.setDataValue?.("qt_b", qtB, GRID_NO_EVENT);
      await formPanel2Ref.current?.setDataValue?.("qt", qt, GRID_NO_EVENT);

      return { qtW, qtB, qt };
    };

    // 행삭제로 항번에 구멍이 생기지 않도록 grid1의 no_line을 1..N으로 다시 매긴다.
    // grid1 행은 모두 insert 상태이고 SnGrid는 insert row를 KeyField로 추적하므로
    // setDataValue 결과가 저장 payload(prd_runl.insert)에 그대로 반영된다.
    const renumberRunlNoLine = () => {
      let seq = 0;
      gridRefs.current["grid1"].current?.forEachNode?.((node: any) => {
        if (!node?.data) return;
        seq += 1;
        if (toNum(node.data.no_line) === seq) return;
        node.setDataValue?.("no_line", seq, GRID_NO_EVENT);
      });
    };

    // 생산실적 행삭제.
    // 삭제 자체는 SnGrid가 처리하고, 여기서는 항번 재정렬 + 실적수량 재계산을 이어서 수행한다.
    const onDeleteRunlRows = async () => {
      const grid1 = gridRefs.current["grid1"].current;
      const selectedRows = grid1?.getSelectedRows?.();
      if (!selectedRows || selectedRows.length < 1) {
        await showDialog("alter", "삭제할 생산실적 행을 선택하세요.");
        restoreBarcodeFocus();
        return;
      }

      const confirmed = await showDialog(
        "confirm",
        "선택한 생산실적을 삭제하시겠습니까?"
      );
      if (!confirmed) {
        restoreBarcodeFocus();
        return;
      }

      grid1?.deleteSelectedRows?.();
      renumberRunlNoLine();
      await recalcRunQtyToPanel();
      setProdBarcode("");
      restoreBarcodeFocus();
    };

    const focusBarcodeInputWithRetry = (attempt = 0) => {
      const input = prodBarcodeRef.current;
      if (input) {
        input.focus();
        input.select?.();
        return;
      }
      if (attempt < 3) {
        setTimeout(() => focusBarcodeInputWithRetry(attempt + 1), 60);
      }
    };
    const restoreBarcodeFocus = () => {
      shouldRestoreProdFocusRef.current = true;
      setTimeout(() => focusBarcodeInputWithRetry(), 60);
      setTimeout(() => focusBarcodeInputWithRetry(), 180);
    };
    const onGridCellFocusedKeepBarcode = () => {
      restoreBarcodeFocus();
    };
    // Grid1 + pending insert rows 기준으로 중복 바코드를 체크한다.
    const isDuplicateBarcode = (barcodeValue: string, fgUnit: string) => {
      const value = (barcodeValue || "").toString().trim();
      if (!value) return false;

      const unit = (fgUnit || "").toString().toUpperCase();
      const key = unit === "SN" ? "no_sn" : unit === "LOT" ? "no_lot" : "";
      const isSame = (row: any) => {
        const byKey = key ? (row?.[key] || "").toString().trim() : "";
        // onBeforeAddRunlRow가 no_barcode로 저장하므로 no_barcode를 비교한다.
        const byBarcode = (row?.no_barcode || "").toString().trim();
        return byKey === value || byBarcode === value;
      };

      let dup = false;
      gridRefs.current["grid1"].current?.forEachNode?.((node: any) => {
        if (isSame(node.data)) {
          dup = true;
        }
      });
      if (dup) return true;

      const changes = gridRefs.current["grid1"].current?.getChange?.() || {};
      const inserts = changes.insert || [];
      if (Array.isArray(inserts) && inserts.some(isSame)) {
        return true;
      }

      return false;
    };

    // 바코드 서버 유효성 체크(fn_prd_run_barcode_check_01).
    // st_proc가 S가 아니면 dc_msg를 표시하고 중단한다.
    const validateBarcode = async (barcodeValue: string) => {
      try {
        const panel1 = formPanel1Ref.current?.getFormdata?.() || {};
        // TODO(공통): WP3001/WP3002/WP3003/WP3004 동일 이슈.
        //   formPanel1Ref -> formPanel2Ref 오타로 fg_run이 항상 빈값으로 전송된다.
        //   4개 팝업이 같은 서버함수를 호출하므로 BE 확인 후 일괄 수정 필요.
        const panel2 = formPanel1Ref.current?.getFormdata?.() || {};
        const payload = {
          id: "fn_prd_run_barcode_check_01",
          params: {
            cd_firm: userInfo.cd_firm,
            no_wo: panel1.no_wo || selectedData?.no_wo,
            cd_item: panel1.cd_item || "",
            cd_op: panel1.cd_op || "",
            cd_op_pre: panel1.cd_op_pre || "",
            fg_run: panel2.fg_run || "",
            no_barcode: barcodeValue,
          },
        };

        const result: any = await fnGetTableById(payload);
        const rows = Array.isArray(result) ? result : [];
        if (rows.length < 1) {
          await showDialog("alter", "바코드 유효성 체크 결과가 없습니다.");
          return null;
        }

        const stProc = (rows[0]?.st_proc || "").toString().toUpperCase();
        if (stProc !== "S") {
          await showDialog(
            "alter",
            rows[0]?.dc_msg || "바코드 유효성 체크에 실패했습니다."
          );
          return null;
        }

        return rows;
      } catch (error: any) {
        await showDialog(
          "error",
          error?.message || "유효성 체크에 실패했습니다."
        );
        return null;
      }
    };

    // 바코드 1건 입력 시 유효성 결과 row 수만큼 runl 행을 추가한다.
    // 최초 스캔에서는 fg_run을 실적정보 패널에 세팅하고, 이후 혼용은 차단한다.
    const addRunlRowByBarcode = async (barcodeValue: string) => {
      const panel2 = formPanel2Ref.current?.getFormdata?.() || {};
      const panel1 = formPanel1Ref.current?.getFormdata?.() || {};
      const fgUnit = (panel1.fg_unit || "").toString();
      const useAutoCarton =
        isCartonBarcodeAutoAvailable && isCartonBarcodeAutoMode;
      const checkRows = await validateBarcode(barcodeValue);
      if (!checkRows) return 0;

      const returnFgRun = (checkRows[0]?.fg_run || "").toString();
      const currentFgRun = (panel2.fg_run || "").toString();
      const displayedCount =
        getGridCurrent("grid1")?.getDisplayedRowCount?.() || 0;
      const isFirstScan = displayedCount === 0;

      if (isFirstScan) {
        if (returnFgRun) {
          await formPanel2Ref.current?.setDataValue?.(
            "fg_run",
            returnFgRun,
            "noEvent"
          );
        }
      } else if (
        returnFgRun &&
        currentFgRun &&
        returnFgRun.toUpperCase() !== currentFgRun.toUpperCase()
      ) {
        await showDialog(
          "alter",
          "[실적구분 혼용 불가] 기존 실적 저장 후 다시 시도해 주세요."
        );
        return 0;
      }

      pendingRunlQueueRef.current = checkRows.map((row: any) => ({
        barcodeValue,
        fgUnit,
        noCt: useAutoCarton ? "" : manualCartonBarcode,
        noRun: panel2.no_run,
        noRunPrev: row?.no_run_pre || "",
      }));

      const addCount = pendingRunlQueueRef.current.length;
      for (let i = 0; i < addCount; i++) {
        gridRefs.current["grid1"].current?.addRow();
      }
      return addCount;
    };

    // 제품바코드 입력 처리.
    // 카톤번호 선입력 -> 중복체크 -> 유효성검사 -> 행추가 -> 실적수량 재계산 순으로 처리한다.
    const handleProductionBarcodeSubmit = async (rawBarcode?: string) => {
      const barcodeValue = (rawBarcode != null ? rawBarcode : prodBarcode)
        .toString()
        .trim();
      if (!barcodeValue) return;
      if (isAutoSavingRef.current) return;

      // 바코드 입력 순서는 스위치 상태로만 결정
      const useAutoCarton =
        isCartonBarcodeAutoAvailable && isCartonBarcodeAutoMode;
      if (useAutoCarton && !nmCartonReport) {
        setProdBarcode("");
        shouldRestoreProdFocusRef.current = true;
        return;
      }
      if (!useAutoCarton && !manualCartonBarcode) {
        setManualCartonBarcode(barcodeValue);
        await formPanel2Ref.current?.setDataValue?.(
          "no_ct",
          barcodeValue,
          "noEvent"
        );
        setProdBarcode("");
        shouldRestoreProdFocusRef.current = true;
        return;
      }

      const panel1 = formPanel1Ref.current?.getFormdata?.() || {};
      const fgUnit = (panel1.fg_unit || "").toString();
      if (isDuplicateBarcode(barcodeValue, fgUnit)) {
        await new Promise((resolve) => setTimeout(resolve, 0));
        await showDialog("alter", "이미 등록된 정보입니다.");
        shouldRestoreProdFocusRef.current = true;
        return;
      }

      const currentPanel2 = formPanel2Ref.current?.getFormdata?.() || {};
      const addedCount = await addRunlRowByBarcode(barcodeValue);
      if (!addedCount) return;

      // 누적이 아니라 grid1 현재 행 기준으로 재계산한다.
      // 행삭제 후 재스캔해도 실적수량이 부풀지 않는다.
      const { qtW: nextQtW } = await recalcRunQtyToPanel();

      setProdBarcode("");
      shouldRestoreProdFocusRef.current = true;
      const qtCt = toNum(panel1Data?.qt_ct ?? currentPanel2?.qt_ct);
      if (qtCt > 0 && nextQtW >= qtCt) {
        await autoSaveAndRefresh();
      }
    };

    // Grid addRow 직전, 대기 큐에서 1건을 꺼내 실제 row 데이터를 구성한다.
    const onBeforeAddRunlRow = () => {
      try {
        const pending = pendingRunlQueueRef.current.shift();
        if (!pending) return {};

        // getMaxValue는 필터/정렬 적용 후 기준이라 숨겨진 행을 놓칠 수 있어
        // 항번 채번에는 필터 무관 최대값을 사용한다.
        const maxNoLine =
          gridRefs.current["grid1"].current?.getNonFilterMaxValue?.(
            "no_line"
          ) ?? 0;
        const fgUnit = (pending.fgUnit || "LOT")
          .toString()
          .trim()
          .toUpperCase();
        const scannedBarcode = (pending.barcodeValue || "").toString();
        const newRow: any = {
          cd_firm: userInfo.cd_firm,
          no_run: pending.noRun,
          no_line: maxNoLine + 1,
          fg_unit: fgUnit,
          no_barcode: scannedBarcode,
          no_ct: (pending.noCt || "").toString(),
          no_run_prev: pending.noRunPrev,
          qt_w: 1,
          qt_b: 0,
          qt: 1,
          ...(fgUnit === "SN"
            ? { no_sn: scannedBarcode, no_lot: "" }
            : { no_sn: "", no_lot: scannedBarcode }),
        };

        return newRow;
      } catch (error: any) {
        pendingRunlQueueRef.current = [];
        return false;
      }
    };

    // 작업일 기준으로 근무일/작업조를 조회한다(fn_prd_dt_work).
    const loadWorkDateInfo = async (dtRun?: string) => {
      const targetDate = (dtRun || dateToString(new Date())).toString();
      const payload = {
        id: "fn_prd_dt_work",
        params: {
          cd_firm: userInfo.cd_firm,
          dt: targetDate,
        },
      };

      const data: any = await fnGetTableById(payload);
      if (Array.isArray(data) && data.length > 0) {
        return {
          dt_run: data[0].dt_work || targetDate,
          cd_shift: data[0].cd_flag || "",
        };
      }

      return {
        dt_run: targetDate,
        cd_shift: "",
      };
    };

    // dt_run 변경 시 작업일/작업조를 재조회해 실적정보 패널 값을 동기화한다.
    const onPanel2ValueChanged = async (event: any) => {
      if (event.colId !== "dt_run") return {};
      const nextDate = (event.newValue || "").toString();
      if (!nextDate) return {};

      try {
        const workInfo = await loadWorkDateInfo(nextDate);
        if (workInfo.dt_run && workInfo.dt_run !== nextDate) {
          await formPanel2Ref.current?.setDataValue?.(
            "dt_run",
            workInfo.dt_run,
            "noEvent"
          );
        }
        await formPanel2Ref.current?.setDataValue?.(
          "cd_shift",
          workInfo.cd_shift || "",
          "noEvent"
        );
      } catch (error: any) {
        showDialog("error", error.message);
      }
      return {};
    };

    useImperativeHandle(ref, () => ({
      getSelectedValue: async () => {
        if (isRunViewMode) {
          return { excelUpload: false };
        }
        const saved = await onSave({ silentSuccess: true });
        if (saved) {
          await onSearch();
          restoreBarcodeFocus();
        }
        return { excelUpload: false };
      },
    }));

    // 탭 전환 시 해당 입력칸으로 포커스를 이동해 스캔 동선을 유지한다.
    useEffect(() => {
      const runCount = getGridCurrent("grid1")?.getDisplayedRowCount?.() || 0;
      // 초기 상태(실적행 없음)에서는 탭이 검사항목이어도 제품바코드부터 시작한다.
      if (runCount < 1) {
        focusProductBarcode();
        return;
      }
      restoreBarcodeFocus();
    }, [tab]);

    useEffect(() => {
      if (!shouldRestoreProdFocusRef.current) return;
      if ((prodBarcode || "").toString().trim()) return;
      shouldRestoreProdFocusRef.current = false;
      restoreBarcodeFocus();
    }, [dataSources.grid1, dataSources.grid2, prodBarcode]);

    useEffect(() => {
      setPopupParentEl(popupRootRef.current);
    }, []);

    // 팝업 최초 오픈: 컨트롤/기본값 세팅 후 초기 조회를 수행한다.
    useEffect(() => {
      const fetchform = async () => {
        try {
          missingCartonReportNotifiedRef.current = false;
          const rtn: any = await getInitPopUpData("SN_PRD_POP_WP3002", {});
          const form1 = rtn.form.form1 || [];
          const form2 = rtn.form.form2 || [];

          const workInfo = await loadWorkDateInfo(dateToString(new Date()));
          const panel2Defaults = {
            dt_run: workInfo.dt_run,
            cd_emp: userInfo.cd_emp,
            nm_emp: userInfo.nm_emp,
            fg_run: selectedData?.fg_run || "S",
            cd_shift: workInfo.cd_shift || "A",
          };

          setPanel1Controls(toReadOnlyControls(form1));
          setPanel2Controls(toReadOnlyControls(form2));
          setPanel1Data(rtn.form.form1ControlData || {});
          setPanel2Data({
            ...(rtn.form.form2ControlData || {}),
            ...panel2Defaults,
          });
          setColums({
            grid1: rtn.grid.grid1 || [],
            grid2: rtn.grid.grid2 || [],
          });
          await new Promise((resolve) => setTimeout(resolve, 0));
          await onSearch();
        } catch (error: any) {
          showDialog("error", error.message);
        }
      };
      fetchform();
    }, []);

    // 첨부파일 키로 쓸 실적번호를 확보한다(조회 시점 선채번).
    const prepareRunNoForAttachment = async () => {
      const selectedNoRun = (selectedData?.no_run || "").toString().trim();
      if (selectedNoRun) {
        setSelectedDocu(selectedNoRun);
        setSelectedLineDocu(0);
        await formPanel2Ref.current?.setDataValue?.(
          "no_run",
          selectedNoRun,
          GRID_NO_EVENT
        );
        return selectedNoRun;
      }

      const runhData = formPanel2Ref.current?.getFormdata?.() || {};
      const dtRun =
        runhData.dt_run || panel2Data?.dt_run || dateToString(new Date());
      const cd_module: any = Menuprops?.cd_module;
      const cd_menu: any = Menuprops?.cd_menu;
      if (!cd_module || !cd_menu || !dtRun) {
        setSelectedDocu("");
        setSelectedLineDocu(0);
        return "";
      }

      const noRun = await getAutoSlip(cd_module, 2, dtRun, cd_menu);
      const nextNoRun = (noRun || "").toString().trim();
      setSelectedDocu(nextNoRun);
      setSelectedLineDocu(0);
      setPanel2Data((prev: any) => ({ ...(prev || {}), no_run: nextNoRun }));
      await formPanel2Ref.current?.setDataValue?.(
        "no_run",
        nextNoRun,
        GRID_NO_EVENT
      );
      return nextNoRun;
    };

    const onSearchRunView = async () => {
      const noRun = (selectedData?.no_run || "").toString().trim();
      if (!noRun) return;

      const params = {
        id: "sn_prd_wrk_reg_001_pop_run_01",
        params: {
          cd_firm: userInfo.cd_firm,
          no_run: noRun,
        },
      };

      const data: any = await fnGetDataSet(params);
      const woHeader = data?.[0]?.[0] || {};
      const runh = data?.[1]?.[0] || {};
      const runlRows = Array.isArray(data?.[2]) ? data[2] : [];
      const inspRows = Array.isArray(data?.[4]) ? data[4] : [];

      setPanel1Data(woHeader);
      setPanel2Data({ ...runh, no_run: noRun });
      pendingRunlQueueRef.current = [];
      setProdBarcode("");
      setIsCartonBarcodeAutoAvailable(false);
      setIsCartonBarcodeAutoMode(false);
      setManualCartonBarcode("");
      hasRunlInspRowsRef.current = inspRows.length > 0;
      setDataSources({
        grid1: runlRows,
        grid2: inspRows,
      });
      setTab(TAB_RUN);
      await prepareRunNoForAttachment();
    };

    // 팝업 기본 조회(지시정보 header + grid1/grid2).
    const onSearch = async () => {
      try {
        if (isRunViewMode) {
          await onSearchRunView();
          return;
        }

        const params = {
          id: "sn_prd_wrk_reg_001_pop_01",
          params: {
            cd_firm: userInfo.cd_firm,
            no_wo: selectedData?.no_wo,
            no_line: selectedData?.no_line,
          },
        };

        const data: any = await fnGetDataSet(params);
        const header = data?.[0]?.[0] || {};
        const isCartonBarcodeAuto = isYnY(header?.yn_carton_barcode_auto);
        const nmCartonReportFromHeader = (header?.nm_carton_report || "")
          .toString()
          .trim();
        // Fixed order contract: [0]=header, [1]=matlCheckList, [2]=partsList, [3]=runlInspList.
        // 이 화면은 투입자재를 다루지 않으므로 runlInspList만 사용한다.
        const { runlInspList } = resolveWrkRegPopupTables(data);
        hasRunlInspRowsRef.current = Array.isArray(runlInspList)
          ? runlInspList.length > 0
          : false;

        setPanel1Data(header);
        setIsCartonBarcodeAutoAvailable(isCartonBarcodeAuto);
        setIsCartonBarcodeAutoMode(isCartonBarcodeAuto);
        setNmCartonReport(nmCartonReportFromHeader);
        setManualCartonBarcode("");
        await formPanel2Ref.current?.setDataValue?.("no_run", "", "noEvent");
        await formPanel2Ref.current?.setDataValue?.("no_ct", "", "noEvent");
        if (
          isCartonBarcodeAuto &&
          !nmCartonReportFromHeader &&
          !missingCartonReportNotifiedRef.current
        ) {
          missingCartonReportNotifiedRef.current = true;
          await showDialog("alter", "카톤라벨양식이 설정되어져 있지 않습니다");
        }
        pendingRunlQueueRef.current = [];
        setProdBarcode("");
        // 아래 빈 배열이 AG Grid에 반영될 때 grid1의 rowDataUpdated가 발생한다.
        // 그 전까지는 재바인딩 대기 상태로 두고, 이후 addRow는 waitForGrid1Rebind 뒤에 한다.
        grid1RebindPendingRef.current = true;
        setDataSources({
          grid1: [],
          grid2: runlInspList,
        });
        setTab(TAB_RUN);
        await prepareRunNoForAttachment();
        // 팝업 오픈/재조회 직후 최초 입력은 항상 제품바코드에서 시작
        focusProductBarcode();
      } catch (error: any) {
        showDialog("error", error.message);
      }
    };

    // 저장: no_run 채번 -> runh/runl/runl_insp payload 구성 -> fnSave.
    const onSave = async (options?: {
      silentNoChanges?: boolean;
      silentSuccess?: boolean;
    }) => {
      try {
        gridRefs.current["grid1"].current?.stopEditing();

        // SnForm은 data prop이 바뀌면 rAF에서 formDataRef를 통째로 덮어쓴다.
        // 자동저장 -> onSearch -> setPanel2Data 직후 팝업 잔량 그룹을 addRow하면
        // recalc로 넣은 qt_w가 지워져 수동 확인 저장 시 카톤번호가 1로 채번되는 문제가 있어
        // 저장 직전에 grid1 기준으로 실적수량을 다시 폼에 반영한다.
        await recalcRunQtyToPanel();

        const runhData = formPanel2Ref.current?.getFormdata?.() || {};
        const panel1Data = formPanel1Ref.current?.getFormdata?.() || {};
        const runlChanges =
          gridRefs.current["grid1"].current?.getChange?.() || {};

        if (!runlChanges.modifiedRowcount || runlChanges.modifiedRowcount < 1) {
          if (!options?.silentNoChanges) {
            await showDialog("alter", "변경된 내용이 없습니다.");
          }
          return false;
        }

        let no_run = runhData.no_run;
        if (!no_run) {
          const cd_module: any = Menuprops?.cd_module;
          const cd_menu: any = Menuprops?.cd_menu;
          if (!cd_module || !cd_menu) {
            await showDialog(
              "error",
              "실적번호 채번 정보가 없습니다. 관리자에게 문의하세요."
            );
            return false;
          }
          if (!runhData.dt_run) {
            await showDialog("alter", "실적일이 없습니다.");
            return false;
          }
          no_run = await getAutoSlip(cd_module, 2, runhData.dt_run, cd_menu);
          if (!no_run) {
            await showDialog("error", "실적번호 채번에 실패했습니다.");
            return false;
          }
        }

        runhData.no_run = no_run;
        if (
          Array.isArray(runlChanges.insert) &&
          runlChanges.insert.length > 0
        ) {
          runlChanges.insert.forEach((row: any) => {
            if (!row.no_run) {
              row.no_run = no_run;
            }
          });
          getGridCurrent("grid1")?.forEachNode?.((node: any) => {
            if (!node.data?.no_run) {
              node.setDataValue("no_run", no_run);
            }
          });
        }

        const runlInspInsert = (getAllGrid2Rows() || []).map((row: any) => ({
          ...row,
          no_run: runhData.no_run,
        }));

        // prd_consh/prd_consl은 이 화면에서 사용하지 않지만,
        // SN_PRD_WRK_REG_001_SAVE_01이 다른 팝업과 공유되는 저장 API라
        // 요청 형태를 동일하게 유지하기 위해 빈 배열로 함께 전송한다.
        const savePayload: any = {
          prd_runh: {
            insert: [
              {
                ...runhData,
                cd_firm: userInfo.cd_firm,
                no_wo: selectedData?.no_wo,
                no_line_wo: selectedData?.no_line,
                cd_op: panel1Data.cd_op,
                st_run: "R",
                cd_item: panel1Data.cd_item,
                yn_auto: runhData?.yn_auto || "N",
                yn_carton_barcode_auto: isCartonBarcodeAutoMode ? "Y" : "N",
                yn_end: panel1Data.yn_end,
                cd_wh: panel1Data.cd_wh,
                cd_gp: userInfo.cd_gp,
                cd_dept: userInfo.cd_dept,
              },
            ],
            update: [],
            delete: [],
          },
          prd_runl: runlChanges,
          prd_consh: { insert: [], update: [], delete: [] },
          prd_consl: { insert: [], update: [], delete: [] },
          prd_runl_insp: { insert: runlInspInsert, update: [], delete: [] },
        };

        const saveResult = await fnSave(
          "SN_PRD_WRK_REG_001_SAVE_01",
          savePayload,
          undefined,
          { silentSuccess: options?.silentSuccess === true }
        );
        if (saveResult === false) {
          return false;
        }
        const saveResultItem =
          saveResult && typeof saveResult === "object" ? saveResult : {};
        const issuedNoCt = (saveResultItem?.no_ct || "").toString().trim();
        const isAutoCartonPrint =
          isCartonBarcodeAutoMode || isYnY(runhData?.yn_carton_barcode_auto);
        if (issuedNoCt) {
          await formPanel2Ref.current?.setDataValue?.(
            "no_ct",
            issuedNoCt,
            "noEvent"
          );
        }
        const shouldPrintAutoCarton =
          isAutoCartonPrint && !!issuedNoCt && !!nmCartonReport;
        if (shouldPrintAutoCarton) {
          try {
            // 카톤 라벨 쿼리는 :no_plt 하나만 사용한다(PRD_LABEL_PRINT 주석 참고).
            await fnPrintByReport(nmCartonReport, {
              no_plt: buildNoPltParam([issuedNoCt]),
            });
          } catch (printError: any) {
            // 로컬 출력 서비스(127.0.0.1:5500) 미동작 시 저장 성공까지 실패로 보이지 않게 분리 처리
            if (options?.silentSuccess !== true) {
              await showDialog(
                "alter",
                "저장은 완료되었습니다. 라벨 출력에 실패했습니다."
              );
            }
          }
        }
        gridRefs.current["grid1"].current?.resetUpdate();
        gridRefs.current["grid2"].current?.resetUpdate();
        restoreBarcodeFocus();
        return true;
      } catch (error: any) {
        showDialog("error", error.message);
        return false;
      }
    };

    const autoSaveAndRefresh = async () => {
      if (isRunViewMode) return false;
      if (hasRunlInspRowsRef.current) return false;
      if (isAutoSavingRef.current) return false;
      isAutoSavingRef.current = true;
      try {
        const saved = await onSave({
          silentNoChanges: true,
          silentSuccess: true,
        });
        if (!saved) return false;
        await onSearch();
        // 재바인딩이 도착하기 전에 호출자가 addRow하면 그 행이 지워지므로 여기서 기다린다.
        await waitForGrid1Rebind();
        restoreBarcodeFocus();
        return true;
      } catch (error) {
        return false;
      } finally {
        isAutoSavingRef.current = false;
      }
    };

    // grid1 rowDataUpdated 핸들러.
    // AG Grid는 rowData 교체뿐 아니라 applyTransaction에도 이 이벤트를 올리므로,
    // onSearch가 플래그를 세운 뒤 첫 이벤트를 재바인딩 도착으로 본다(그 사이 grid1 트랜잭션은 없다).
    const onGrid1RowDataUpdated = () => {
      grid1RebindPendingRef.current = false;
      const resolve = grid1RebindResolverRef.current;
      grid1RebindResolverRef.current = null;
      resolve?.();
    };

    // onSearch의 grid1 빈 배열 재바인딩이 AG Grid에 반영될 때까지 기다린다.
    // 이미 반영됐으면(플래그 false) 즉시 반환한다.
    // 타이머 상한은 두지 않는다. onSearch는 항상 새 배열을 넣어 SnGrid effect -> AgGridReact rowData 교체 ->
    // rowDataUpdated가 반드시 이어지고, 유일한 미도착 경로인 대기 중 언마운트는 아래 cleanup이 풀어준다.
    const waitForGrid1Rebind = () => {
      if (!grid1RebindPendingRef.current) return Promise.resolve();
      return new Promise<void>((resolve) => {
        grid1RebindResolverRef.current = resolve;
      });
    };

    // 대기 중 팝업이 닫히면 grid1이 언마운트되어 이벤트가 오지 않으므로 pending Promise를 풀어준다.
    useEffect(() => {
      return () => {
        grid1RebindPendingRef.current = false;
        const resolve = grid1RebindResolverRef.current;
        grid1RebindResolverRef.current = null;
        resolve?.();
      };
    }, []);

    // 자동저장 후 onSearch의 setDataSources({grid1: []})는 React state -> SnGrid effect -> AG Grid rowData
    // 순으로 비동기 반영된다. 팝업 적용처럼 그룹을 연속 처리하면 이전 그룹 행이 아직 남은 그리드 위에
    // 다음 그룹이 addRow되어 recalcRunQtyToPanel(forEachNode 합산)이 부풀고, 그 qt_w로 카톤번호가
    // 채번되는 문제(48 -> 96)가 있었다. 지금은 autoSaveAndRefresh가 재바인딩 도착까지 기다리므로
    // 정상 경로에서는 no-op이고, 그리드가 비어 있지 않은 예외 상황의 안전장치로 남긴다.
    const clearGrid1Rows = () => {
      const grid1 = gridRefs.current["grid1"].current;
      const staleRows: any[] = [];
      grid1?.forEachNode?.((node: any) => {
        if (node?.data) staleRows.push(node.data);
      });
      if (staleRows.length > 0) {
        grid1?.applyTransaction?.({ remove: staleRows });
      }
    };

    const onSnButtonClick = async (id: string) => {
      switch (id) {
        case "btn_POP1":
          if (!isCartonBarcodeAutoMode && manualCartonBarcode === "") {
            await showDialog("alter", "카톤번호를 먼저 입력해 주세요.");
            return;
          }

          // if (getGridCurrent("grid1").getDisplayedRowCount() !== 0) {
          //   await showDialog("alter", "등록중인 건이 있습니다.");
          //   return;
          // }
          const component = (
            <WP3002_POP_001
              selectedData={selectedData}
              scanedRowCount={gridRefs.current[
                "grid1"
              ].current?.getDisplayedRowCount()}
              isAutoCarton={isCartonBarcodeAutoMode}
            />
          );
          const result = await showResizablePopupModal({
            title: "직전공정실적적용",
            children: component,
            validation: true,
          });

          if (!result || result === "close") return;

          const barcodeResult: any[] = result[0] || [];

          const pop1Panel1 = formPanel1Ref.current?.getFormdata?.() || {};
          const pop1Panel2 = formPanel2Ref.current?.getFormdata?.() || {};
          const pop1FgUnit = (pop1Panel1.fg_unit || "").toString();
          const pop1UseAutoCarton =
            isCartonBarcodeAutoAvailable && isCartonBarcodeAutoMode;

          const pop1Targets = barcodeResult
            .map((row: any) => ({
              barcode: (row?.no_sn || row?.no_lot || row?.no_barcode || "")
                .toString()
                .trim(),
              noRunPrev: (row?.no_run || "").toString(), // 팝업 행의 실적번호 = 직전공정 실적번호
            }))
            .filter(
              (t) => t.barcode && !isDuplicateBarcode(t.barcode, pop1FgUnit)
            );
          if (pop1Targets.length < 1) return;

          // 스캔과 동일하게 포장단위수량(qt_ct)씩 끊어 카톤 단위로 순차 자동저장한다.
          // 여러 그룹 선택은 자동카톤 모드에서만 가능(수동카톤은 POP_001이 잔량만큼만 선택 허용).
          const pop1QtCt = toNum(panel1Data?.qt_ct ?? pop1Panel2?.qt_ct);
          const chunkSize = pop1QtCt > 0 ? pop1QtCt : pop1Targets.length;
          setIsSaving(true);
          try {
            for (
              let start = 0;
              start < pop1Targets.length;
              start += chunkSize
            ) {
              const group = pop1Targets.slice(start, start + chunkSize);

              // 그룹 저장 후 재조회에서 no_run이 새로 채번되므로 반드시 그룹마다 폼에서 다시 읽는다.
              const groupPanel2 = formPanel2Ref.current?.getFormdata?.() || {};

              pendingRunlQueueRef.current = group.map((t) => ({
                barcodeValue: t.barcode,
                fgUnit: pop1FgUnit,
                noCt: pop1UseAutoCarton ? "" : manualCartonBarcode,
                noRun: groupPanel2.no_run,
                noRunPrev: t.noRunPrev,
              }));
              for (let i = 0; i < group.length; i++) {
                gridRefs.current["grid1"].current?.addRow();
              }

              // 누적이 아닌 grid1 현재 행 기준 재계산 → qt_ct 도달 시 자동저장
              const { qtW: groupQtW } = await recalcRunQtyToPanel();
              if (pop1QtCt > 0 && groupQtW >= pop1QtCt) {
                // autoSaveAndRefresh가 grid1 재바인딩 도착까지 기다린 뒤 돌아오므로
                // 다음 그룹 addRow가 뒤늦은 재바인딩에 지워지지 않는다.
                const saved = await autoSaveAndRefresh();
                // 재바인딩 뒤에도 행이 남아 있는 예외 상황에 대비해 비운다(qt_w 부풀림 방지).
                if (saved) {
                  clearGrid1Rows();
                }
              } else {
                // 마지막 그룹이 qt_ct 미만이면 스캔과 동일하게 저장하지 않고 그리드에 남긴다.
                restoreBarcodeFocus();
              }
            }
          } finally {
            // 어떤 경로로 끝나든 스피너는 반드시 닫는다.
            setIsSaving(false);
          }
          break;
      }
    };

    const grids = useMemo(
      () => ({
        grid1: {
          columns: columns.grid1,
          dataSource: dataSources.grid1,
          events: {
            onBeforeAddRow: onBeforeAddRunlRow,
            onCellFocused: onGridCellFocusedKeepBarcode,
            onRowDataUpdated: onGrid1RowDataUpdated,
          },
        },
        grid2: {
          columns: columns.grid2,
          dataSource: dataSources.grid2,
          events: {
            onCellFocused: onGridCellFocusedKeepBarcode,
          },
        },
      }),
      [columns, dataSources]
    );

    return (
      <div
        ref={popupRootRef}
        className="h-full flex flex-col gap-2 overflow-hidden"
      >
        {/* Backdrop은 닫혀 있어도 DOM에 남으므로 space-y 대신 gap을 쓴다
            (absolute 요소는 flex item이 아니라 gap 계산에서 제외되어 간격이 안 밀림) */}
        <CommonSpinner isLoading={isSaving} />
        <div className="p-2 min-h-[190px]">
          <SnTab
            titles={["지시정보"]}
            width={"100%"}
            selectedTab={"1"}
            onTabChanged={() => {}}
            header={
              <div className="flex justify-end gap-2">
                <SnButton
                  visible={false}
                  text="작업표준서"
                  onClick={() => {
                    showDialog("alter", "작업표준서 기능은 준비중입니다.");
                  }}
                />
                <SnButton
                  visible={false}
                  text="작업사양서"
                  onClick={() => {
                    showDialog("alter", "작업사양서 기능은 준비중입니다.");
                  }}
                />
              </div>
            }
            body={[
              <SnForm
                ref={formPanel1Ref}
                data={panel1Data}
                controls={panel1Controls}
              />,
            ]}
          />
        </div>
        <div className="p-2 min-h-[150px]">
          <SnTab
            titles={["실적정보"]}
            width={"100%"}
            selectedTab={"1"}
            onTabChanged={() => {}}
            body={[
              <SnForm
                ref={formPanel2Ref}
                data={panel2Data}
                controls={panel2Controls}
                etcProps={{ gridAlign: "start", allowXScroll: false }}
                events={{ onValueChanged: onPanel2ValueChanged }}
              />,
            ]}
          />
        </div>
        <div className="flex-1 min-h-0 flex w-full" ref={panel2WrapperRef}>
          <div className="min-h-0 h-full w-full">
            <SnTab
              titles={["생산실적", "검사항목"]}
              width={"100%"}
              selectedTab={tab}
              onTabChanged={(index: any) => {
                setTab(index);
              }}
              header={[
                <div className="flex items-center gap-2 p-1 w-full justify-between flex-nowrap">
                  {/* 스캔칸/자동카톤 스위치는 탭 라벨 옆에 붙이고,
                      행삭제 버튼은 다른 그리드와 동일하게 우측 끝에 둔다. */}
                  <div className="flex items-center gap-2 flex-nowrap">
                    <div>
                      <SnButton
                        text={"직전공정실적적용"}
                        onClick={() => {
                          onSnButtonClick("btn_POP1");
                        }}
                      />
                    </div>
                    <div className="w-44">
                      <SnBarcodeBox
                        popupRef={popupRootRef}
                        inputRef={prodBarcodeRef}
                        isReadOnly={isRunViewMode}
                        onEnter={handleProductionBarcodeSubmit}
                      />
                    </div>
                    <SnSwitch
                      labelCheck="자동카톤"
                      labelUncheck="수동카톤"
                      checked={isCartonBarcodeAutoMode}
                      disabled={isRunViewMode || !isCartonBarcodeAutoAvailable}
                      onChange={(_, checked) => {
                        setIsCartonBarcodeAutoMode(checked);
                        if (checked) {
                          setManualCartonBarcode("");
                          formPanel2Ref.current?.setDataValue?.(
                            "no_ct",
                            "",
                            "noEvent"
                          );
                        }
                      }}
                    />
                  </div>
                  <SnTabHeader
                    onBtnDeleteClick={
                      isRunViewMode
                        ? undefined
                        : () => {
                            onDeleteRunlRows();
                          }
                    }
                  />
                </div>,
                <div className="w-full" />,
              ]}
              body={[
                <div className="min-h-0 h-full w-full flex flex-col">
                  <SnGrid
                    ref={gridRefs.current["grid1"]}
                    popupParent={popupParentEl || undefined}
                    columnConfig={grids.grid1.columns}
                    rowData={grids.grid1.dataSource}
                    etcProps={{
                      rowSelection: "single",
                      isEditable: !isRunViewMode,
                    }}
                    events={grids.grid1.events}
                  />
                </div>,
                <div className="min-h-0 h-full w-full flex flex-col">
                  <SnGrid
                    ref={gridRefs.current["grid2"]}
                    popupParent={popupParentEl || undefined}
                    columnConfig={grids.grid2.columns}
                    rowData={grids.grid2.dataSource}
                    etcProps={{
                      rowSelection: "single",
                    }}
                    events={grids.grid2.events}
                  />
                </div>,
              ]}
            />
          </div>
        </div>
        <div className="px-2 pb-2">
          <SnFileUploadBox
            selectedDocu={selectedDocu}
            selectedLineDocu={selectedLineDocu}
          />
        </div>
      </div>
    );
  }
);

export default WP3002;
